import { z } from "zod";
import { EXPENSE_STATUS } from "./expenses.js";
import {
  EXPENSE_STATUS_WITH_COOLING,
  expenseStatusWithCoolingSchema,
  type ExpenseStatusWithCooling,
} from "./cooling-freeze-anomaly.js";

/**
 * Shared DTO cho Admin Expenses Oversight (US-AE5.F6-0093 · @UC9164) — liệt kê TOÀN BỘ khoản chi
 * xuyên quỹ/nhóm (mọi status: draft|pending_decision|approved|rejected|posted|reversed), READ-ONLY.
 * Một nguồn sự thật cho api (S03 — list+filter+full-text) và admin web (S04 — render), tránh drift
 * shape ở 2 nơi (DRY). Mirror style admin-consensus.ts NHƯNG list bảng `expenses` TRỰC TIẾP (không
 * qua phiên biểu quyết) ⇒ mọi khoản chi, mọi status, có category/amount/date/full-text filter.
 *
 * BẢO MẬT (invariants — LOAD-BEARING):
 * - REDACTION PII: DTO KHÔNG khai báo `createdBy` (userId người tạo) / `receiptKeys` (R2 key biên lai)
 *   / `description` thô. `.strict()` reject mọi field lạ tại biên. 4 vai admin xem cùng metadata.
 * - B1: `amountMinor` = BigInt minor-units → STRING (regex số nguyên, no float/number JS). NULLABLE
 *   KHÔNG áp (amountMinor NOT NULL ở schema) — nhưng để null-safe join, giữ nullable ở item.
 * - C3: schema `.strict()` — KHÔNG khai báo secret / PII; field lạ bị reject tại biên.
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 * - 🔴 US-AE16.F1-0369 (@UC9630) enum `status` = REUSE `expenseStatusWithCoolingSchema`
 *   (`cooling-freeze-anomaly.ts` — KHÔNG định nghĩa lại, DRY). **8 giá trị**, mirror CHECK db
 *   `expenses_status_check`: draft|pending_decision|approved|rejected|posted|reversed|cooling|cancelled.
 *   TRƯỚC 0369 chỗ này dùng `expenseStatusSchema` (6 giá trị của contract CLIENT) ⇒ mọi khoản chi
 *   `cooling`/`cancelled` làm `map-expense.parse` ném ZodError ⇒ `GET /v1/admin/expenses` **500
 *   VĨNH VIỄN** (sort `createdAt DESC` ⇒ dòng độc ở trang đầu ⇒ chết cả màn). Đây là drift 2
 *   whitelist, cùng họ txType. Mở rộng phải ADDITIVE — status lạ VẪN 400.
 *
 * Envelope khớp quy ước list repo: `{ data, pageInfo: { hasNext, nextCursor } }`.
 */

/** Max page-size (data lớn → 100). Default (20) do backend áp khi vắng (normalizeLimit). */
export const ADMIN_EXPENSES_MAX_LIMIT = 100;

/**
 * Re-export whitelist status của surface ADMIN (8 giá trị = CHECK db) cho consumer tiện dùng —
 * nguồn gốc `cooling-freeze-anomaly.ts`. `EXPENSE_STATUS` giữ lại vì UI cũ còn tham chiếu hằng tên.
 */
export { EXPENSE_STATUS, EXPENSE_STATUS_WITH_COOLING, expenseStatusWithCoolingSchema };
export type { ExpenseStatusWithCooling };

/** Số nguyên minor-units dạng string (BigInt-safe, KHÔNG float). Dùng cho amount range + item amount. */
const MONEY_INTEGER_STRING = /^\d+$/;

/**
 * `status` filter đa-giá-trị (multi-select). Nhận:
 *  - 1 giá trị đơn: `status=posted`
 *  - CSV: `status=posted,rejected`
 *  - repeated param: `status=posted&status=rejected` (Express gộp thành array)
 * → normalize thành `ExpenseStatusWithCooling[]`. Mỗi phần tử phải ∈ EXPENSE_STATUS_WITH_COOLING
 * (8 giá trị = CHECK db) — reject giá trị lạ. Trước 0369 chỉ nhận 6 ⇒ `?status=cooling` ăn 400.
 */
const statusListSchema = z
  .union([z.string(), z.array(z.string())])
  .transform((v) =>
    (Array.isArray(v) ? v : v.split(",")).map((s) => s.trim()).filter((s) => s.length > 0),
  )
  .pipe(z.array(expenseStatusWithCoolingSchema).min(1))
  .optional();

/** amount range phần tử: string số nguyên minor (BigInt-safe). */
const amountMinorField = z.string().regex(MONEY_INTEGER_STRING, "amount phải là số nguyên minor units").optional();

/**
 * Query list (AC1-AC4 filter server-side + full-text + cursor):
 *  - `fundId?` / `groupId?` (uuid) — lọc theo quỹ / nhóm.
 *  - `status?` — 1|CSV|array ∈ EXPENSE_STATUS_WITH_COOLING (vắng ⇒ TOÀN BỘ trạng thái, AC4).
 *  - `category?` — chuỗi ≤100.
 *  - `from?`/`to?` — ISO datetime, lọc createdAt range (refine from ≤ to).
 *  - `amountMin?`/`amountMax?` — string minor-units (refine min ≤ max).
 *  - `q?` — full-text name+description (≤256), server-side ILIKE contains.
 *  - `cursor?` opaque, `limit?` ∈ [1,100].
 * Validate tại biên (Zod) — single source api ↔ admin.
 */
export const adminExpensesQuerySchema = z
  .object({
    fundId: z.string().uuid().optional(),
    groupId: z.string().uuid().optional(),
    status: statusListSchema,
    category: z.string().trim().min(1).max(100).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    amountMin: amountMinorField,
    amountMax: amountMinorField,
    q: z.string().trim().min(1).max(256).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_EXPENSES_MAX_LIMIT).optional(),
  })
  .strict()
  .refine((v) => !(v.from && v.to) || Date.parse(v.from) <= Date.parse(v.to), {
    message: "from phải ≤ to",
    path: ["from"],
  })
  .refine((v) => !(v.amountMin && v.amountMax) || BigInt(v.amountMin) <= BigInt(v.amountMax), {
    message: "amountMin phải ≤ amountMax",
    path: ["amountMin"],
  });

export type AdminExpensesQuery = z.infer<typeof adminExpensesQuerySchema>;

/**
 * `amountMinor` = BigInt minor-units → string (B1, no float) HOẶC null (null-safe). regex chỉ nhận
 * chuỗi số nguyên (dấu âm cho phép — reversal/điều chỉnh có thể âm) — reject float `"123.45"`.
 */
const amountMinorString = z
  .string()
  .regex(/^-?\d+$/, "amountMinor phải là số nguyên minor units (không float)")
  .nullable()
  .optional();

/**
 * List item (AC1). `name`/`category`/`fundName`/`groupName`/`amountMinor` NULLABLE+OPTIONAL
 * (join miss / cột nullable schema). `status` = EXPENSE_STATUS_WITH_COOLING (**8 giá trị** = CHECK
 * db). KHÔNG BAO GIỜ chứa `createdBy` / `receiptKeys` / `description` thô (`.strict()` reject field
 * lạ tại biên — PII).
 *
 * 🔴 US-AE16.F1-0369-S02 (@UC9631 @UC9632) thêm 2 trường ĐỌC-THÊM:
 *  - `postAfter` — mốc worker sẽ TỰ ghi sổ khoản chi `cooling`. Không có nó, admin thấy dòng
 *    "chờ ghi sổ" mà **không biết bao giờ tiền rời quỹ**. KHÔNG phải số tiền ⇒ mọi vai xem được.
 *  - `payerMemberId` — NGƯỜI ỨNG TIỀN (chủ nợ thật của peer-debt sinh từ khoản chi).
 *    🔴 Đọc lại đúng cột đã KHAI TƯỜNG MINH lúc tạo — **TUYỆT ĐỐI KHÔNG suy từ người bấm duyệt**
 *    (CLAUDE.md §3.6, bug đắt nhất repo). NULL ⇒ trả `null`, không đoán.
 *    Là DANH TÍNH ⇒ redact theo vai ở TẦNG SERVER; quy ước lane admin: ẩn = `null`.
 */
export const adminExpenseItemSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    fundId: z.string(),
    fundName: z.string().nullable().optional(),
    groupId: z.string(),
    groupName: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    amountMinor: amountMinorString,
    currency: z.string(),
    status: expenseStatusWithCoolingSchema,
    createdAt: z.string(),
    postAfter: z.string().nullable(),
    payerMemberId: z.string().nullable(),
  })
  .strict();

export type AdminExpenseItem = z.infer<typeof adminExpenseItemSchema>;

/** Envelope khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext). */
export const adminExpensesResponseSchema = z.object({
  data: z.array(adminExpenseItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminExpensesResponse = z.infer<typeof adminExpensesResponseSchema>;
